import moment from 'moment';
// Explicitly import and register French locale – avoid tree-shaking
import frLocaleData from 'moment/locale/fr';

// ── Moment locale ────────────────────────────────────────────────
// Force-register the French locale with the imported data
moment.defineLocale('fr', frLocaleData as any);
moment.locale('fr');
moment.updateLocale('fr', {
  week: { dow: 1, doy: 4 },
});

export { moment };

// ── react-big-calendar shared config ─────────────────────────────
export const calendarMessages = {
  allDay: 'Journée',
  previous: 'Précédent',
  next: 'Suivant',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucune visite dans cette période',
  showMore: (total: number) => `+ ${total} de plus`,
};

/** Min / Max times for week & day views: full 24 h */
export function calendarMinTime(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
export function calendarMaxTime(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Common formats – 24 h, French, capitalize */
export const calendarFormats = {
  weekdayFormat: (date: Date) => moment(date).format('dddd'),
  dayFormat: (date: Date) => moment(date).format('dddd DD/MM/YYYY'),
  monthHeaderFormat: (date: Date) => moment(date).format('MMMM YYYY'),
  dayHeaderFormat: (date: Date) => moment(date).format('dddd D MMMM'),
  dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
    `${moment(start).format('D MMMM')} – ${moment(end).format('D MMMM YYYY')}`,
  timeGutterFormat: (date: Date) => moment(date).format('HH:mm'),
  eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
    `${moment(start).format('HH:mm')} – ${moment(end).format('HH:mm')}`,
};

// ── French public holidays (dynamic computation) ─────────────────

/** Compute Easter Sunday for a given year (Anonymous Gregorian algorithm) */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export interface FrenchHoliday {
  date: Date;
  label: string;
}

/** Return all French public holidays for a given year */
export function getFrenchHolidays(year: number): FrenchHoliday[] {
  const easter = easterSunday(year);
  return [
    { date: new Date(year, 0, 1), label: "Jour de l'An" },
    { date: addDays(easter, 1), label: 'Lundi de Pâques' },
    { date: new Date(year, 4, 1), label: 'Fête du Travail' },
    { date: new Date(year, 4, 8), label: 'Victoire 1945' },
    { date: addDays(easter, 39), label: 'Ascension' },
    { date: addDays(easter, 50), label: 'Lundi de Pentecôte' },
    { date: new Date(year, 6, 14), label: 'Fête nationale' },
    { date: new Date(year, 7, 15), label: 'Assomption' },
    { date: new Date(year, 10, 1), label: 'Toussaint' },
    { date: new Date(year, 10, 11), label: 'Armistice 1918' },
    { date: new Date(year, 11, 25), label: 'Noël' },
  ];
}

/** Check whether a date is a French public holiday; returns label or null */
export function isHoliday(date: Date): string | null {
  const holidays = getFrenchHolidays(date.getFullYear());
  const match = holidays.find(
    (h) =>
      h.date.getFullYear() === date.getFullYear() &&
      h.date.getMonth() === date.getMonth() &&
      h.date.getDate() === date.getDate(),
  );
  return match?.label ?? null;
}
